import { Controller } from "@hotwired/stimulus"
import { shouldActivateClickEvent } from "../../../utils/keyboardAccessibility"

const ADD_MANAGE_TODO_MODAL_ID = "quiz-results-add-manage-todo-modal"
const CREATE_LIST_MODAL_CONTROLLER = "create-list-modal"
const LIST_CHECKBOX_TARGET_PREFIX = "aquifer-ui-quiz-results-todo-form.listCheckboxes"
const TOGGLE_DISABLED_FLAG = "quizResultsTodoFormToggleDisabled"
const TOGGLE_DISABLED_SELECTOR = "[data-quiz-results-todo-form-toggle-disabled='true']"
const MY_TODOS_LIST_LABEL = "My To-Dos"

export default class extends Controller {
  static targets = [
    "todoListCheckboxes",
    "checkbox",
    "heading",
    "selectLists",
    "formSubmit",
    "errorContainer",
    "noCheckbox",
  ]

  connect() {
    if (this.noCheckboxTarget.dataset.persisted != "true") {
      const checkbox = this.myTodosListCheckbox("todoListCheckboxes-0")
      if (checkbox) checkbox.checked = true
    }

    this.updateCheckboxes(`${LIST_CHECKBOX_TARGET_PREFIX}-0`, "heading-0")
    this.updateCheckboxes(`${LIST_CHECKBOX_TARGET_PREFIX}-1`, "heading-1")
    this.updateCheckboxes(`${LIST_CHECKBOX_TARGET_PREFIX}-2`, "heading-2")
    this.boundHandleClickOutside = this.handleClickOutside.bind(this)
    this.boundListenKeyTaps = this.listenKeyTaps.bind(this)
    this.boundListenKeyUps = this.listenKeyUps.bind(this)
    document.addEventListener("click", this.boundHandleClickOutside)
    document.addEventListener("keydown", this.boundListenKeyTaps)
    document.addEventListener("keyup", this.boundListenKeyUps, true)

    this.syncAllFieldContainers()
  }

  disconnect() {
    document.removeEventListener("click", this.boundHandleClickOutside)
    document.removeEventListener("keydown", this.boundListenKeyTaps)
    document.removeEventListener("keyup", this.boundListenKeyUps, true)
  }

  toggleTodoListCheckboxes(event) {
    event.preventDefault()
    event.stopPropagation()

    const trigger = event.currentTarget
    const todoListCheckboxes = document.getElementById(trigger.dataset.target)
    if (!todoListCheckboxes) return

    const isOpen = !todoListCheckboxes.classList.contains("hidden")

    if (isOpen) {
      this.closeListDropdown(trigger, todoListCheckboxes)
      return
    }

    this.openListDropdown(trigger, todoListCheckboxes)
  }

  openCreateListModal(event) {
    if (!shouldActivateClickEvent(event)) return

    event.preventDefault()
    event.stopPropagation()

    const panel = event.currentTarget.closest(".quiz-results-todo-fields__dropdown-panel")
    if (!panel) return

    const index = panel.dataset.index
    const dropdown = this.targets.find(`dropdown-${index}`)
    this.closeListDropdown(dropdown, panel)

    const currentName = this.targets.find(`createListNameField-${index}`)?.value || ""
    this.createListModalController()?.openWithIndex(index, { currentName })
  }

  applyNewListName(index, listName) {
    const trimmedName = listName.trim()
    if (!trimmedName) return

    this.ensureIncludeItemChecked(index)

    const panel = document.getElementById(`todoListCheckboxes-${index}`)
    const existingCheckbox = this.findListCheckboxByName(panel, trimmedName)
    const createFlag = this.targets.find(`createNewListFlag-${index}`)
    const createInput = this.targets.find(`createListNameField-${index}`)

    if (existingCheckbox) {
      existingCheckbox.checked = true
      if (createFlag) createFlag.value = "0"
      if (createInput) createInput.value = ""
    } else {
      if (createFlag) createFlag.value = "1"
      if (createInput) createInput.value = trimmedName
    }

    this.updateCheckboxes(
      `${LIST_CHECKBOX_TARGET_PREFIX}-${index}`,
      `heading-${index}`,
      `dropdown-${index}`,
      existingCheckbox ? null : { target: { value: trimmedName } }
    )
  }

  ensureIncludeItemChecked(index) {
    const checkboxElement = this.targets.find(`item-checkbox-${index}`)
    const includeCheckbox = this.itemCheckboxInput(checkboxElement)
    if (!includeCheckbox || includeCheckbox.checked) return

    includeCheckbox.checked = true
    const container = this.fieldContainer(includeCheckbox.dataset.fieldContainer)
    if (container) {
      this.setContainerFieldsDisabled(container, false)
    }
  }

  findListCheckboxByName(panel, listName) {
    if (!panel) return null

    const normalizedName = listName.trim().toLowerCase()

    return (
      Array.from(panel.querySelectorAll(".dropdown-menu-checkbox-label")).find(
        (label) => label.textContent.trim().toLowerCase() === normalizedName
      )?.closest("label")
        ?.querySelector('input[type="checkbox"]') || null
    )
  }

  updateSelectedLists(event) {
    this.updateCheckboxes(
      event.target.dataset.target,
      event.currentTarget.dataset.heading,
      event.currentTarget.dataset.dropdown,
      event
    )
  }

  toggleInputFields(event) {
    const includeCheckbox = event.target
    const container = this.fieldContainer(includeCheckbox.dataset.fieldContainer)
    if (container) {
      this.setContainerFieldsDisabled(container, !includeCheckbox.checked)
    }

    if (
      includeCheckbox.checked &&
      this.targets.find(includeCheckbox.dataset.heading).textContent == "Select list"
    ) {
      const myTodoCheckbox = this.myTodosListCheckbox(includeCheckbox.dataset.checkboxContainer)
      if (myTodoCheckbox) {
        myTodoCheckbox.checked = true
        this.updateCheckboxes(
          myTodoCheckbox.dataset.target,
          includeCheckbox.dataset.heading,
          includeCheckbox.dataset.dropdown
        )
      }
    }
  }

  updateCheckboxes(checkboxSelector, headingSelector, dropdownSelector, event) {
    const checkboxes = document.querySelectorAll(`[data-target="${checkboxSelector}"]`)
    const heading = this.targets.find(headingSelector)
    const dropdown = dropdownSelector ? this.targets.find(dropdownSelector) : null
    const createFlag = dropdownSelector
      ? this.targets.find(`createNewListFlag-${dropdownSelector.replace("dropdown-", "")}`)
      : null

    if (checkboxes.length === 0 && !createFlag) return

    let selectedNames = Array.from(checkboxes)
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => this.checkboxLabel(checkbox))
      .filter(Boolean)
      .join(", ")

    if (createFlag?.value === "1") {
      const createInput = this.targets.find(`createListNameField-${dropdownSelector?.replace("dropdown-", "")}`)
      const newListName = event?.target?.value?.trim() || createInput?.value?.trim()

      if (newListName) {
        selectedNames = selectedNames ? `${selectedNames}, ${newListName}` : newListName
      }
    }

    if (dropdown) {
      dropdown.classList.remove("dropdown-field-control-error")
      dropdown.classList.toggle("dropdown-field-control-filled", Boolean(selectedNames))
      this.clearListDropdownError(dropdown)
    }

    checkboxes.forEach((checkbox) => {
      checkbox.closest('[role="option"]')?.setAttribute("aria-selected", String(checkbox.checked))
    })

    if (heading) {
      heading.textContent = selectedNames || "Select list"
    }
  }

  checkboxLabel(checkbox) {
    const label = checkbox.closest("label")?.querySelector(".dropdown-menu-checkbox-label")
    return label?.textContent.trim() || checkbox.nextElementSibling?.textContent.trim() || ""
  }

  openListDropdown(trigger, panel) {
    if (!trigger || !panel) return

    panel.classList.remove("hidden")
    trigger.setAttribute("aria-expanded", "true")
    trigger.classList.add("dropdown-field-control-focus")
    this.announceListStatus(panel)
  }

  closeListDropdown(trigger, panel) {
    if (!trigger || !panel) return

    panel.classList.add("hidden")
    trigger.setAttribute("aria-expanded", "false")
    trigger.removeAttribute("aria-activedescendant")
    trigger.classList.remove("dropdown-field-control-focus")
  }

  hideTodoModal(event) {
    this.modalController()?.close(event)
  }

  createListModalController() {
    const root = document.querySelector(`[data-controller~="${CREATE_LIST_MODAL_CONTROLLER}"]`)
    if (!root) return null

    return this.application.getControllerForElementAndIdentifier(root, CREATE_LIST_MODAL_CONTROLLER)
  }

  modalController() {
    const modalRoot = document.getElementById(ADD_MANAGE_TODO_MODAL_ID)
    if (!modalRoot) return null

    return this.application.getControllerForElementAndIdentifier(modalRoot, "railsui-modal")
  }

  itemCheckboxInput(element) {
    return element?.querySelector('input[type="checkbox"]')
  }

  fieldContainer(targetClass) {
    if (!targetClass) return null

    return document.getElementsByClassName(targetClass)[0] || null
  }

  myTodosListCheckbox(panelId) {
    const panel = document.getElementById(panelId)
    if (!panel) return null

    const label = Array.from(panel.querySelectorAll(".dropdown-menu-checkbox-label, label")).find(
      (element) => element.textContent.trim() === MY_TODOS_LIST_LABEL
    )
    if (!label) return null

    const checkbox = label.closest("label")?.querySelector('input[type="checkbox"]') ||
      (label.htmlFor ? document.getElementById(label.htmlFor) : null)

    return checkbox?.type === "checkbox" ? checkbox : null
  }

  syncAllFieldContainers() {
    ;[0, 1, 2].forEach((index) => {
      const checkboxElement = this.targets.find(`item-checkbox-${index}`)
      const includeCheckbox = this.itemCheckboxInput(checkboxElement)
      const container = this.fieldContainer(includeCheckbox?.dataset.fieldContainer)

      if (container && includeCheckbox) {
        this.setContainerFieldsDisabled(container, !includeCheckbox.checked)
      }
    })
  }

  setContainerFieldsDisabled(container, disabled) {
    container.classList.toggle("disabled", disabled)

    if (disabled) {
      container.querySelectorAll("input, textarea, select, button").forEach((element) => {
        if (element.matches(".quiz-results-todo-fields__dropdown-toggle")) return

        this.disableElement(element)
      })

      container.querySelectorAll(".date-input-aq").forEach((wrapper) => {
        this.disableDateFieldWrapper(wrapper)
      })

      this.disableDropdownToggles(container)
      this.closeTodoListDropdowns(container)
      return
    }

    container.querySelectorAll(TOGGLE_DISABLED_SELECTOR).forEach((element) => {
      this.enableElement(element)
    })

    this.enableDropdownToggles(container)
  }

  disableDropdownToggles(container) {
    container.querySelectorAll(".quiz-results-todo-fields__dropdown-toggle").forEach((button) => {
      button.dataset[TOGGLE_DISABLED_FLAG] = "true"
      button.disabled = true
      button.setAttribute("aria-disabled", "true")
    })
  }

  enableDropdownToggles(container) {
    container.querySelectorAll(".quiz-results-todo-fields__dropdown-toggle").forEach((button) => {
      button.disabled = false
      button.removeAttribute("disabled")
      button.setAttribute("aria-disabled", "false")
      delete button.dataset[TOGGLE_DISABLED_FLAG]
    })
  }

  disableElement(element) {
    if (element.disabled) return

    element.dataset[TOGGLE_DISABLED_FLAG] = "true"
    element.disabled = true
  }

  enableElement(element) {
    if (element.dataset[TOGGLE_DISABLED_FLAG] !== "true") return

    if (element.classList.contains("date-input-aq")) {
      element.classList.remove("date-input-aq--disabled")
    } else {
      element.disabled = false
    }

    delete element.dataset[TOGGLE_DISABLED_FLAG]
  }

  disableDateFieldWrapper(wrapper) {
    if (wrapper.classList.contains("date-input-aq--disabled")) return

    wrapper.dataset[TOGGLE_DISABLED_FLAG] = "true"
    wrapper.classList.add("date-input-aq--disabled")
  }

  closeTodoListDropdowns(container) {
    container.querySelectorAll('[id^="todoListCheckboxes-"]').forEach((panel) => {
      const index = panel.id.replace("todoListCheckboxes-", "")
      const dropdown = this.targets.find(`dropdown-${index}`)

      this.closeListDropdown(dropdown, panel)
    })
  }

  handleClickOutside(event) {
    ;[0, 1, 2].forEach((index) => {
      const panel = document.getElementById(`todoListCheckboxes-${index}`)
      const dropdown = this.targets.find(`dropdown-${index}`)

      if (panel && !panel.classList.contains("hidden") && !panel.contains(event.target) && !dropdown?.contains(event.target)) {
        this.closeListDropdown(dropdown, panel)
      }
    })
  }

  submitTodoForm(event) {
    this.errorContainerTarget.classList.add("hidden")
    let errors = false
    let checked = false

    ;[0, 1, 2].forEach((index) => {
      const element = this.targets.find(`item-checkbox-${index}`)
      const dropdown = this.targets.find(`dropdown-${index}`)
      const includeCheckbox = this.itemCheckboxInput(element)

      if (element && includeCheckbox?.checked) {
        checked = true

        if (this.targets.find(`heading-${index}`).textContent == "Select list") {
          errors = true
          dropdown.classList.add("dropdown-field-control-error")
          this.showListDropdownError(dropdown, index)
        }
      }
    })

    if (errors || !checked) {
      event.preventDefault()
      if (!checked) {
        this.errorContainerTarget.classList.remove("hidden")
        this.errorContainerTarget.innerHTML = "Select at least one to-do item to proceed."
      }
    } else if (this.element.dataset.demoTodo === "true") {
      // ponytail: UI-only Patient Library demo; INT-3 persists TodoItems.
      event.preventDefault()
      this.hideTodoModal()
    } else {
      // Close without passing the click event so modal close does not call preventDefault
      // and block the submit button's native form submission (legacy hideTodoModal behavior).
      this.hideTodoModal()
    }
  }

  listenKeyTaps(event) {
    if (this.handleListDropdownKey(event)) return

    if (event.key === "Escape" || event.key === "Esc" || event.keyCode === 27) {
      return
    }
    if (event.key !== "Enter" && event.keyCode !== 13) return
    if (this.shouldIgnoreEnterSubmit(event.target)) return

    this.formSubmitTarget.click()
  }

  listenKeyUps(event) {
    if (!this.blockModalEscape) return
    if (event.key !== "Escape" && event.key !== "Esc" && event.keyCode !== 27) return

    event.preventDefault?.()
    event.stopPropagation?.()
    event.stopImmediatePropagation?.()
    this.blockModalEscape = false
  }

  handleListDropdownKey(event) {
    const isEscape = event.key === "Escape" || event.key === "Esc" || event.keyCode === 27
    if (isEscape) {
      if (this.closeOpenListDropdowns({ restoreFocus: true })) {
        event.preventDefault?.()
        event.stopPropagation?.()
        event.stopImmediatePropagation?.()
        this.blockModalEscape = true
        return true
      }
      return false
    }

    const trigger = this.listTriggerFrom(event.target)
    const panel = this.listPanelFrom(event.target)
    if (!trigger || !panel) return false

    const isOpen = !panel.classList.contains("hidden")

    if (["ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault?.()
      if (!isOpen) this.openListDropdown(trigger, panel)
      this.moveListOptionFocus(panel, event.key === "ArrowDown" ? 1 : -1, event.target)
      return true
    }

    const option = event.target?.closest?.('[role="option"]')
    if ((event.key === "Enter" || event.key === " ") && option) {
      event.preventDefault?.()
      this.toggleListOption(option)
      return true
    }

    if ((event.key === "Enter" || event.key === " ") && event.target === trigger) {
      event.preventDefault?.()
      this.toggleTodoListCheckboxes({
        preventDefault() {},
        stopPropagation() {},
        currentTarget: trigger,
      })
      if (!isOpen) this.moveListOptionFocus(panel, 1, trigger)
      return true
    }

    return false
  }

  listPanelFrom(target) {
    if (!(target instanceof HTMLElement)) return null
    if (target.matches(".quiz-results-todo-fields__dropdown-toggle")) {
      return document.getElementById(target.dataset.target)
    }
    return target.closest(".quiz-results-todo-fields__dropdown-panel")
  }

  listTriggerFrom(target) {
    if (!(target instanceof HTMLElement)) return null
    if (target.matches(".quiz-results-todo-fields__dropdown-toggle")) return target

    const panel = target.closest(".quiz-results-todo-fields__dropdown-panel")
    const index = panel?.dataset?.index
    return index == null ? null : this.targets.find(`dropdown-${index}`)
  }

  listboxOptions(panel) {
    return Array.from(panel?.querySelectorAll('[role="option"]:not([aria-disabled="true"])') || []).filter(
      (option) => !option.querySelector("input:disabled")
    )
  }

  moveListOptionFocus(panel, direction, currentTarget) {
    const options = this.listboxOptions(panel)
    if (!options.length) return

    const currentIndex = options.findIndex(
      (option) => option === currentTarget || option.contains(currentTarget)
    )
    const nextIndex =
      currentIndex === -1
        ? direction > 0
          ? 0
          : options.length - 1
        : (currentIndex + direction + options.length) % options.length

    options.forEach((option) => {
      option.tabIndex = -1
    })
    const next = options[nextIndex]
    next.tabIndex = 0
    next.focus()
    this.listTriggerFrom(panel.querySelector('[role="option"]') || currentTarget)
      ?.setAttribute("aria-activedescendant", next.id)
  }

  toggleListOption(option) {
    const checkbox = option.querySelector('input[type="checkbox"]')
    if (!checkbox || checkbox.disabled) return

    checkbox.checked = !checkbox.checked
    checkbox.dispatchEvent(new Event("change", { bubbles: true }))
    option.setAttribute("aria-selected", String(checkbox.checked))
  }

  announceListStatus(panel) {
    const status = panel.querySelector("[aria-live]")
    if (!status) return

    const count = this.listboxOptions(panel).length
    status.textContent = count === 1 ? "1 list available" : `${count} lists available`
  }

  closeOpenListDropdowns({ restoreFocus = false } = {}) {
    let closed = false

    ;[0, 1, 2].forEach((index) => {
      const panel = document.getElementById(`todoListCheckboxes-${index}`)
      const dropdown = this.targets.find(`dropdown-${index}`)

      if (panel && !panel.classList.contains("hidden")) {
        this.closeListDropdown(dropdown, panel)
        if (restoreFocus) dropdown?.focus()
        closed = true
      }
    })

    return closed
  }

  showListDropdownError(dropdown, index) {
    if (!dropdown) return

    const errorId = `todo-list-error-${index}`
    dropdown.setAttribute("aria-invalid", "true")
    dropdown.setAttribute("aria-describedby", errorId)
    document.getElementById(errorId)?.classList.remove("hidden")
  }

  clearListDropdownError(dropdown) {
    if (!dropdown) return

    const errorId = dropdown.getAttribute("aria-describedby")
    dropdown.removeAttribute("aria-invalid")
    dropdown.removeAttribute("aria-describedby")
    if (errorId) document.getElementById(errorId)?.classList.add("hidden")
  }

  shouldIgnoreEnterSubmit(target) {
    if (!(target instanceof HTMLElement)) return false

    if (
      target.matches(
        "textarea, select, button, a[href], [role='button'], [role='combobox'], [contenteditable='true']"
      )
    ) {
      return true
    }

    const tag = target.tagName
    if (tag === "INPUT") {
      const type = (target.getAttribute("type") || "text").toLowerCase()
      return !["text", "search", "email", "tel", "url", "password", "number"].includes(type)
    }

    return Boolean(
      target.closest("button, a[href], [role='button'], [role='listbox'], [role='option']")
    )
  }
}
